import * as React from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import {
  MdAdd,
  MdListAlt,
  MdClose,
  MdDelete,
  MdTaskAlt,
  MdAutorenew,
  MdSearch,
  MdVisibility,
  MdHourglassTop,
  MdEdit,
  MdWarning,
} from 'react-icons/md';
import DataGrid from '../../Components/Table/DataGrid';
import DeleteModel from '../../Components/Common/DeleteModel';
import { useNavigate } from "react-router-dom";
import { APPRoutes } from '../../Utils/Route';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { toast } from 'react-toastify';
import { getAllTrackings, deleteTracking, getAllFollowUps } from '../../Services/TrackingService';
import PageTitle from '../../Components/Common/PageTitle';

const isRowOverdue = (row) => {
  if (!row) return false;
  const overdueCount = Number(row.OverdueTrackCount ?? row.overdue_trackings ?? 0);
  if (overdueCount > 0) return true;
  if (row.is_overdue || row.IsOverdue) return true;
  const statusStr = String(row.StatusName || row.Status || '').trim().toLowerCase();
  if (statusStr === 'overdue' || statusStr.includes('severely overdue')) return true;
  const tracks = Array.isArray(row.tracks) ? row.tracks : [];
  return tracks.some((track) => {
    const overdueDays = Number(track?.overdue_days ?? track?.OverdueDays ?? 0);
    return overdueDays > 0 || !!track?.overdue_type || !!track?.OverdueType || !!track?.overdue_category || !!track?.OverdueCategory;
  });
};

export const resolveEffectiveFollowUp = (rowOrGroup, followUpOptions = []) => {
  const tracks = Array.isArray(rowOrGroup?.tracks)
    ? rowOrGroup.tracks
    : Array.isArray(rowOrGroup?.Trackings)
      ? rowOrGroup.Trackings
      : Array.isArray(rowOrGroup?.trackings)
        ? rowOrGroup.trackings
        : [];

  const getFId = (t) => String(t?.follow_up_id ?? t?.FollowUpId ?? t?.followUpId ?? t?.follow_up?.id ?? t?.FollowUp?.Id ?? t?.id ?? '');
  const getFName = (t) => t?.follow_up_name ?? t?.FollowUpName ?? t?.follow_up?.name ?? t?.FollowUp?.Name ?? t?.follow_up ?? t?.followUp ?? '';
  const isCompleted = (t) =>
    t?.is_monitoring_completed === true ||
    t?.is_monitoring_completed === 'true' ||
    t?.is_monitoring_completed === 1 ||
    String(t?.is_monitoring_completed).toLowerCase() === 'true' ||
    t?.IsMonitoringCompleted === true ||
    t?.IsMonitoringCompleted === 1 ||
    String(t?.IsMonitoringCompleted).toLowerCase() === 'true';

  if (!tracks.length) {
    const fId = rowOrGroup?.EffectiveFollowUpId ?? rowOrGroup?.follow_up_id ?? rowOrGroup?.FollowUpId ?? null;
    return {
      followUpId: fId ? String(fId) : '',
      followUpName: rowOrGroup?.EffectiveFollowUpName ?? rowOrGroup?.FollowUpName ?? rowOrGroup?.follow_up_name ?? '',
      track: null,
    };
  }

  const rowStatus = (rowOrGroup?.StatusName ?? rowOrGroup?.status ?? rowOrGroup?.Status ?? tracks[0]?.status_name ?? tracks[0]?.StatusName ?? '').toString().toLowerCase().replace(/-/g, ' ').trim();
  const isActive = rowStatus === 'active' || rowStatus === 'completed';

  // If Active/Completed, the effective step is the one where is_monitoring_completed was marked true
  if (isActive) {
    const completedTrack = tracks.find(isCompleted);
    if (completedTrack) {
      return {
        followUpId: getFId(completedTrack),
        followUpName: getFName(completedTrack),
        track: completedTrack,
      };
    }
  }

  // If In Progress (or active without explicit flag), pick the last followup where is_monitoring_completed is false
  const falseTracks = tracks.filter((t) => !isCompleted(t));
  const candidateTracks = falseTracks.length > 0 ? falseTracks : tracks;

  // Check order against followUpOptions sequence if available
  if (Array.isArray(followUpOptions) && followUpOptions.length > 0) {
    const followUpIdOrderMap = new Map();
    followUpOptions.forEach((item, idx) => {
      const val = String(item?.value ?? item?.id ?? '');
      if (val && val !== 'all') {
        followUpIdOrderMap.set(val, idx);
      }
    });

    let bestTrack = null;
    let maxIdx = -1;

    for (const t of candidateTracks) {
      const fid = getFId(t);
      const idx = followUpIdOrderMap.has(fid) ? followUpIdOrderMap.get(fid) : -1;
      if (idx > maxIdx) {
        maxIdx = idx;
        bestTrack = t;
      }
    }

    if (maxIdx >= 0 && bestTrack) {
      return {
        followUpId: getFId(bestTrack),
        followUpName: getFName(bestTrack),
        track: bestTrack,
      };
    }
  }

  // Fallback: sort candidate tracks by date / id descending
  const sortedTracks = [...candidateTracks].sort((a, b) => {
    const dateA = new Date(a?.created_on || a?.CreatedOn || a?.modified_on || a?.ModifiedOn || 0).getTime();
    const dateB = new Date(b?.created_on || b?.CreatedOn || b?.modified_on || b?.ModifiedOn || 0).getTime();
    if (!Number.isNaN(dateA) && !Number.isNaN(dateB) && dateA !== dateB) {
      return dateB - dateA;
    }
    const idA = Number(a?.id ?? a?.Id ?? 0);
    const idB = Number(b?.id ?? b?.Id ?? 0);
    return idB - idA;
  });

  const targetTrack = sortedTracks[0] || candidateTracks[0];
  return {
    followUpId: getFId(targetTrack),
    followUpName: getFName(targetTrack),
    track: targetTrack,
  };
};

const STATUS_OPTIONS = ['All', 'Active', 'In Progress', 'Overdue'];
const STATUS_DROPDOWN_OPTIONS = STATUS_OPTIONS.map((option) => ({
  value: option,
  label: option,
}));
const STATUS_BADGE_STYLE = {
  Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)' },
  'In Progress': { backgroundColor: 'var(--hc-icon-blue-alt-bg)', color: 'var(--hc-icon-blue-alt)' },
  Overdue: { backgroundColor: 'var(--hc-status-danger-bg)', color: 'var(--hc-status-danger-text)' },
  'Due Soon': { backgroundColor: 'var(--hc-status-pending-list-bg)', color: 'var(--hc-icon-amber-text)' },
  'Severely Overdue': { backgroundColor: 'var(--hc-status-danger-bg)', color: 'var(--hc-status-danger-text-strong)' },
};

export default function TrackingList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canViewTracking = permissionHas(userPermissionCodes, PermissionCodes.CREDENTIALING_TRACKING_VIEW, decodedToken);
  const canCreateTracking = permissionHas(userPermissionCodes, PermissionCodes.CREDENTIALING_TRACKING_CREATE, decodedToken);
  const canEditTracking = permissionHas(userPermissionCodes, PermissionCodes.CREDENTIALING_TRACKING_EDIT, decodedToken);
  const canDeleteTracking = permissionHas(userPermissionCodes, PermissionCodes.CREDENTIALING_TRACKING_DELETE, decodedToken);

  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [followUpFilter, setFollowUpFilter] = React.useState('all');
  const [followUpOptions, setFollowUpOptions] = React.useState([{ value: 'all', label: 'All' }]);
  const [rows, setRows] = React.useState([]);
  const [statusSummary, setStatusSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');

  const handleCreateMonitoring = React.useCallback(() => {
    navigate(APPRoutes.CREDENTIALING_TRACKING_FORM);
  }, [navigate]);

  const loadTrackings = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllTrackings(token);
      if (response?.is_success) {
        const raw = response?.data?.data ?? response?.data ?? response;

        // Map stats (if provided) into the statusSummary shape used by the UI
        const stats = raw?.stats || raw?.StatusSummary || raw?.statusSummary || null;
        const mappedSummary = stats
          ? {
            Total: stats.total ?? stats.Total ?? 0,
            Active: stats.active ?? stats.Active ?? 0,
            'In Progress': stats.in_progress ?? stats['in_progress'] ?? stats['In Progress'] ?? 0,
            Overdue: stats.overdue ?? stats.Overdue ?? 0,
          }
          : null;

        const rawTrackings = Array.isArray(raw?.trackings)
          ? raw.trackings
          : Array.isArray(raw?.Trackings)
            ? raw.Trackings
            : Array.isArray(raw?.data?.trackings)
              ? raw.data.trackings
              : Array.isArray(raw?.data?.Trackings)
                ? raw.data.Trackings
                : [];
        const flattened = rawTrackings.map((group) => {
          const provider = group?.provider || group?.Provider || {};
          const payor = group?.healthcare_payor || group?.HealthCarePayor || {};
          const tracks = Array.isArray(group?.tracks)
            ? group.tracks
            : Array.isArray(group?.Trackings)
              ? group.Trackings
              : Array.isArray(group?.trackings)
                ? group.trackings
                : [];
          const firstTrack = tracks[0] ?? null;

          const providerId = firstTrack?.provider_id ?? firstTrack?.ProviderId ?? provider?.id ?? provider?.Id;
          const payorId = firstTrack?.healthcare_payor_id ?? firstTrack?.HealthcarePayorId ?? firstTrack?.healthcarePayorId ?? payor?.id ?? payor?.Id;

          const overdueTracks = tracks.filter((track) => {
            const overdueDays = Number(track?.overdue_days ?? track?.OverdueDays ?? 0);
            return overdueDays > 0 || !!track?.overdue_type || !!track?.OverdueType || !!track?.overdue_category || !!track?.OverdueCategory;
          });

          const overdueFollowUpIds = overdueTracks
            .map((track) => String(track?.follow_up_id ?? track?.FollowUpId ?? track?.followUpId ?? track?.id ?? ''))
            .filter(Boolean);
          const overdueFollowUpNames = overdueTracks
            .map((track) => track?.follow_up_name ?? track?.FollowUpName ?? track?.follow_up ?? track?.followUp ?? '')
            .filter(Boolean);

          const allFollowUpIds = tracks
            .map((track) => String(track?.follow_up_id ?? track?.FollowUpId ?? track?.followUpId ?? track?.follow_up?.id ?? track?.FollowUp?.Id ?? track?.id ?? ''))
            .filter(Boolean);
          const allFollowUpNames = tracks
            .map((track) => track?.follow_up_name ?? track?.FollowUpName ?? track?.follow_up?.name ?? track?.FollowUp?.Name ?? track?.follow_up ?? track?.followUp ?? '')
            .filter(Boolean);

          const effective = resolveEffectiveFollowUp(group);

          return {
            Id: firstTrack?.id ?? firstTrack?.Id ?? `${providerId || 'unknown'}-${payorId || 'unknown'}`,
            provider_id: providerId,
            healthcare_payor_id: payorId,
            ProviderName:
              ((provider?.first_name || provider?.FirstName || '') +
                (provider?.last_name || provider?.LastName ? ` ${provider?.last_name || provider?.LastName}` : '')).trim(),
            PayorName: payor?.payor_name ?? payor?.PayorName ?? payor?.name ?? payor?.Name,
            StatusName: group?.status ?? group?.Status ?? firstTrack?.status_name ?? firstTrack?.StatusName ?? firstTrack?.status ?? firstTrack?.Status ?? '',
            ManagedBy:
              firstTrack?.created_by_name ?? firstTrack?.createdByName ?? firstTrack?.created_by ?? firstTrack?.createdBy ?? provider?.personal_email ?? provider?.personalEmail ?? '',
            FollowUpName: effective?.followUpName || (firstTrack?.follow_up_name ?? firstTrack?.followUpName ?? firstTrack?.follow_up ?? firstTrack?.followUp ?? null),
            EffectiveFollowUpId: effective?.followUpId || '',
            EffectiveFollowUpName: effective?.followUpName || '',
            OverdueType: firstTrack?.overdue_type ?? firstTrack?.OverdueType ?? firstTrack?.overdue_category ?? firstTrack?.OverdueCategory ?? null,
            TrackCount: group?.total_trackings ?? tracks.length,
            OverdueTrackCount: group?.overdue_trackings ?? overdueTracks.length,
            OverdueFollowUpIds: overdueFollowUpIds,
            OverdueFollowUpNames: overdueFollowUpNames,
            FollowUpIds: allFollowUpIds,
            FollowUpNames: allFollowUpNames,
            tracks,
            provider,
            payor,
          };
        });
        setRows(flattened);
        setStatusSummary(mappedSummary);
      } else if (response?.status_code === 500) {
        toast.error("Internal Server Error. Please Contact Support Team.");
        setRows([]);
        setStatusSummary(null);
      } else {
        setRows([]);
        setStatusSummary(null);
      }
    } catch (error) {
      console.error(error);
      setRows([]);
      setStatusSummary(null);
    } finally {
      setLoading(false);
    }
  }, [token]);


  const loadFollowUps = React.useCallback(async () => {
    try {
      const response = await getAllFollowUps(token);
      if (response?.is_success) {
        const raw = response?.data?.data ?? response?.data ?? response;
        const options = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.FollowUps)
            ? raw.FollowUps
            : Array.isArray(raw?.followups)
              ? raw.followups
              : Array.isArray(raw?.follow_ups)
                ? raw.follow_ups
                : [];

        const mappedOptions = [
          { value: 'all', label: 'All' },
          ...options.map((item) => ({
            value: String(item?.id ?? item?.Id ?? item?.follow_up_id ?? item?.FollowUpId ?? item?.followUpId ?? item?.followUp ?? item?.follow_up ?? item?.name ?? item?.Name ?? item?.label ?? ''),
            label: item?.name ?? item?.Name ?? item?.follow_up_name ?? item?.followUpName ?? item?.label ?? 'Unnamed',
          }))
            .filter((option) => option.value),
        ];

        setFollowUpOptions(mappedOptions);
      } else {
        setFollowUpOptions([{ value: 'all', label: 'All' }]);
      }
    } catch (error) {
      console.error(error);
      setFollowUpOptions([{ value: 'all', label: 'All' }]);
    }
  }, [token]);

  React.useEffect(() => {
    if (token) {
      loadTrackings();
      loadFollowUps();
    }
  }, [token, loadTrackings, loadFollowUps]);



  const handleDeleteClick = React.useCallback((row) => {
    setDeleteError('');
    setDeleteTarget(row);
  }, []);

  const handleDeleteClose = React.useCallback(() => {
    if (deleteLoading) {
      return;
    }
    setDeleteTarget(null);
    setDeleteError('');
  }, [deleteLoading]);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!deleteTarget || !token) {
      return;
    }

    const deleteId = deleteTarget.provider_id && deleteTarget.healthcare_payor_id
      ? `${deleteTarget.provider_id}~${deleteTarget.healthcare_payor_id}`
      : deleteTarget.Id;

    try {
      setDeleteLoading(true);
      setDeleteError('');
      const response = await deleteTracking(deleteId, token);
      if (response?.is_success) {
        await loadTrackings();
        setDeleteTarget(null);
      } else if (response?.status_code === 500) {
        toast.error("Internal Server Error. Please Contact Support Team.");
        setDeleteError('Internal Server Error. Please Contact Support Team.');
      } else {
        setDeleteError(response?.message || 'Failed to delete the monitoring. Please try again.');
      }
    } catch (error) {
      console.error(error);
      setDeleteError('Something went wrong while deleting the monitoring.');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, token, loadTrackings]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const rowStatus = (row.StatusName || row.Status || '').toString().trim();
      let matchesStatus = false;

      if (status === 'All') {
        matchesStatus = true;
      } else if (status === 'Active') {
        matchesStatus = rowStatus.toLowerCase() === 'active';
      } else if (status === 'In Progress') {
        const normalized = rowStatus.toLowerCase().replace(/-/g, ' ');
        matchesStatus = normalized.includes('in progress');
      } else if (status === 'Overdue') {
        matchesStatus = isRowOverdue(row);
      }

      if (!matchesStatus) {
        return false;
      }

      if (followUpFilter && followUpFilter !== 'all') {
        const effective = resolveEffectiveFollowUp(row, followUpOptions);
        const effectiveId = String(effective?.followUpId || row.EffectiveFollowUpId || '');
        const overdueIds = Array.isArray(row.OverdueFollowUpIds) ? row.OverdueFollowUpIds.map(String) : [];
        const filterStr = String(followUpFilter);

        let matchesFollowUp = false;
        if (status === 'Overdue') {
          matchesFollowUp = overdueIds.includes(filterStr) || effectiveId === filterStr;
        } else {
          matchesFollowUp = effectiveId === filterStr;
        }

        if (!matchesFollowUp) {
          return false;
        }
      }

      const matchesSearch =
        !term ||
        row.ProviderName?.toLowerCase().includes(term) ||
        row.PayorName?.toLowerCase().includes(term) ||
        row.ManagedBy?.toLowerCase().includes(term);

      return matchesSearch;
    });
  }, [rows, search, status, followUpFilter, followUpOptions]);

  const stats = React.useMemo(() => {
    const counts = statusSummary || rows.reduce((acc, row) => {
      const rowStatus = row.StatusName || row.Status || 'Unknown';
      acc[rowStatus] = (acc[rowStatus] || 0) + 1;
      return acc;
    }, {});

    return [
      {
        label: 'Total Monitorings',
        value: rows.length,
        icon: <MdListAlt size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Active Monitorings',
        value: counts.Active || 0,
        icon: <MdTaskAlt size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'In Progress Monitorings',
        value: counts['In Progress'] || 0,
        icon: <MdAutorenew size={22} color="var(--hc-icon-blue-alt)" />,
        iconBg: 'var(--hc-icon-blue-alt-bg)',
        accentClass: 'border-info',
        valueClass: 'text-info',
      },
      {
        label: 'Overdue Monitorings',
        value: counts.Overdue || 0,
        icon: <MdHourglassTop size={22} color="var(--hc-status-danger-text)" />,
        iconBg: 'var(--hc-status-danger-bg)',
        accentClass: 'border-danger',
        valueClass: 'text-danger',
      },
    ];
  }, [rows, statusSummary]);

  const columns = React.useMemo(() => [
    {
      id: 'ProviderName',
      label: 'Provider',
      minWidth: 180,
      format: (value, row) => {
        const isOverdue = isRowOverdue(row);
        const overdueCount = Number(row.OverdueTrackCount ?? 0);
        const tooltipText = overdueCount > 0
          ? `${overdueCount} overdue follow-up${overdueCount > 1 ? 's' : ''}`
          : 'Overdue';

        return (
          <div className="d-inline-flex align-items-center flex-nowrap" style={{ verticalAlign: 'middle', lineHeight: 1 }}>
            {isOverdue && (
              <span
                className="text-danger d-inline-flex align-items-center justify-content-center flex-shrink-0"
                title={tooltipText}
                style={{ cursor: 'help', lineHeight: 1, marginRight: '6px' }}
              >
                <MdWarning size={14} className="text-danger" style={{ display: 'block' }} />
              </span>
            )}
            {canViewTracking ? (
              <button
                type="button"
                className="btn p-0 text-start text-dark border-0 bg-transparent"
                style={{ font: 'inherit', lineHeight: 'inherit' }}
                onClick={() => {
                  const idParam = encodeURIComponent(`${row.provider_id}~${row.healthcare_payor_id}`);
                  navigate(APPRoutes.CREDENTIALING_TRACKING_VIEW.replace(':id', idParam), {
                    state: { tracking: row },
                  });
                }}
              >
                {value || 'N/A'}
              </button>
            ) : (
              <span className="text-dark" style={{ lineHeight: 'inherit' }}>{value || 'N/A'}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'PayorName',
      label: 'HealthCare Payor',
      minWidth: 180,
    },
    {
      id: 'StatusName',
      label: 'Status',
      minWidth: 100,
      align: 'center',
      format: (value) => (
        <span
          className="badge rounded-pill fw-bolder px-3 py-2 text-center"
          style={{
            minWidth: '96px',
            fontWeight: 1000,
            ...(STATUS_BADGE_STYLE[value] || {
              backgroundColor: 'var(--hc-status-inactive-bg)',
              color: 'var(--hc-status-inactive-text)',
            }),
          }}
        >
          {value}
        </span>
      ),
    },
    {
      id: 'ManagedBy',
      label: 'Created By',
      minWidth: 140,
    },
    ...(canViewTracking || canEditTracking || canDeleteTracking ? [{
      id: 'actions',
      label: 'Actions',
      minWidth: 100,
      align: 'center',
      format: (_value, row) => (
        <div className="d-flex align-items-center justify-content-center gap-2">
          {canViewTracking ? (
            <button
              type="button"
              title="View"
              className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
              style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
              onClick={() => {
                const idParam = encodeURIComponent(`${row.provider_id}~${row.healthcare_payor_id}`);
                navigate(APPRoutes.CREDENTIALING_TRACKING_VIEW.replace(':id', idParam), {
                  state: { tracking: row },
                });
              }}
            >
              <MdVisibility size={16} />
            </button>
          ) : null}
          {canEditTracking ? (
            <button
              type="button"
              title="Edit"
              className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
              style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
              onClick={() => {
                const idParam = encodeURIComponent(`${row.provider_id}~${row.healthcare_payor_id}`);
                navigate(APPRoutes.CREDENTIALING_TRACKING_EDIT.replace(':id', idParam));
              }}
            >
              <MdEdit size={16} />
            </button>
          ) : null}
          {canDeleteTracking ? (
            <button
              type="button"
              title="Delete"
              className="btn btn-sm p-1 lh-1 text-danger border-0 rounded-2"
              style={{ backgroundColor: 'var(--hc-status-danger-bg)' }}
              onClick={() => handleDeleteClick(row)}
            >
              <MdDelete size={16} />
            </button>
          ) : null}
        </div>
      ),
    }] : []),
  ], [navigate, handleDeleteClick, canViewTracking, canEditTracking, canDeleteTracking]);

  return (
    <>
      <PageTitle title="Monitoring - Aegis 360"/>
      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Monitoring</h4>
          <p className="text-secondary mb-0 small">
            Manage all monitoring and track the status of the credentialing process
          </p>
        </div>
        {canCreateTracking ? (
          <button
            onClick={handleCreateMonitoring}
            type="button"
            className="btn hc-bg-primary mt-md-0 mt-2 w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
          >
            <MdAdd size={20} />
            <span>Create Monitoring</span>
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="row g-3 mb-3">
          {[1, 2, 3].map((_, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg">
              <div className="bg-white p-4 rounded-4 shadow">
                <div className="skeleton mb-2" style={{ height: 16, width: "60%" }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: "40%" }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3 mb-3">
          {stats.map((stat) => (
            <div key={stat.label} className="col-12 col-sm-6 col-lg-3">
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div
                  className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`}
                />
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3"
                  style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
                >
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm">
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                    {stat.label}
                  </span>
                  <span className={`fw-bold fs-5 ${stat.valueClass}`}>
                    {stat.value}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="d-flex flex-column flex-md-row align-items-start gap-2 mb-3">
          <div className="flex-grow-1">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search By Provider, Payor or Managed By"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="flex-shrink-0">
            <FormControl size="small" style={{ minWidth: 200 }}>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="flex-shrink-0">
            <FormControl size="small" style={{ minWidth: 200 }}>
              <InputLabel id="followup-label">Follow-up</InputLabel>
              <Select
                labelId="followup-label"
                label="Follow-up"
                value={followUpFilter}
                onChange={(event) => setFollowUpFilter(event.target.value)}
              >
                {followUpOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <div>
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="d-flex gap-3 align-items-center mb-3"
                >
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "20%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "20%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "15%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "15%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "15%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "15%" }} />
                </div>
              ))}
            </div>
          ) : (
            <DataGrid
              columns={columns}
              rows={filteredRows}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.Id}
            />
          )}
        </div>
      </div>

      <DeleteModel
        isOpen={Boolean(deleteTarget)}
        title="Delete Monitoring"
        entityLabel="monitoring"
        itemName={deleteTarget?.ProviderName || ''}
        description="You are about to remove this monitoring record from the platform. This action is permanent."
        confirmText="Delete Monitoring"
        isLoading={deleteLoading}
        errorMessage={deleteError}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}